import Phaser from 'phaser';
import { t } from '../i18n';
import { GOTHIC, MONO, PALETTE, hex } from '../render/palette';
import { drawTurtle } from '../render/turtle';
import type { RunMode } from './MenuScene';

export const VIEW_W = 540;
export const VIEW_H = 960;

/**
 * あそびかた。
 *
 * **見分け方こそが、このゲームの中身。** 乗る波と潜る波の違い、
 * 拾えるものと避けるものの違いが分からなければ、操作を覚えても遊べない。
 * 実際に初めて遊んだ人から「どの波に乗ってどの波を潜るのか分からない」
 * 「子ガメや宝やイルカが何なのか分からない」と返ってきた。
 *
 * **絵は本編と同じ描き方で出す。** ここで別の絵を描くと、覚えたものが
 * 海の中で見つからない。色の意味は 2 つだけ —
 * **緑は味方、オレンジは危険。** その 2 つだけ持ち帰ってもらう。
 * 色の名前は当てにせず、**実際の色を横に置いて**指し示す。
 *
 * 走行中には出さない。ここは「一度読みに来る場所」で、
 * 遊びながら要る手引きは `tutorial.ts` がその場面で 1 行ずつ出す。
 */

/** 1 項目の高さ。絵と 2 行の文字が収まる。 */
const ROW_H = 92;
const ROW_TOP = 152;
/** 絵を描く帯の幅。左端に置き、文字は右へ。 */
const ART_X = 34;
const ART_W = 96;
const TEXT_X = ART_X + ART_W + 18;
/** 色見本の左端。 */
const SWATCH_X = 96;

/** 日本語の字。空白で戻してよいかの判定に使う。 */
const CJK = /[\u3000-\u30ff\u3400-\u9fff\uff00-\uffef]/;
/** 行頭に置かない字。ここだけは前の行にぶら下げる。 */
const NO_LINE_START = new Set([...'。、．，）」』】〉’”%？！ー']);

type Row = {
  /** 左の絵。中心 (cx, cy) に、本編と同じ描き方で描く。 */
  readonly art: (g: Phaser.GameObjects.Graphics, cx: number, cy: number, timeMs: number) => void;
  readonly title: string;
  readonly body: string;
};

export class HowToScene extends Phaser.Scene {
  private g!: Phaser.GameObjects.Graphics;
  private rows: Row[] = [];
  private back: { scene: string; stageId?: string; mode?: RunMode } = { scene: 'menu' };
  private colorSwatchY = 0;

  constructor() {
    super('howto');
  }

  create(data?: { back?: string; stageId?: string; mode?: RunMode }): void {
    this.back = {
      scene: data?.back === 'game' ? 'game' : 'menu',
      ...(data?.stageId ? { stageId: data.stageId } : {}),
      ...(data?.mode ? { mode: data.mode } : {}),
    };
    // Phaser はシーンのインスタンスを使い回す。貯め込む配列は毎回空にする。
    this.rows = [];

    this.cameras.main.setBackgroundColor(PALETTE.deep);
    this.g = this.add.graphics();

    this.add
      .text(VIEW_W / 2, 56, t().howToTitle, {
        fontFamily: GOTHIC,
        fontSize: '30px',
        color: hex(PALETTE.swell),
      })
      .setOrigin(0.5, 0);
    this.add
      .text(VIEW_W / 2, 100, t().howToLead, {
        fontFamily: GOTHIC,
        fontSize: '15px',
        color: hex(PALETTE.inkDim),
        align: 'center',
      })
      .setOrigin(0.5, 0);

    this.rows = [
      { art: (g, x, y) => this.artRideable(g, x, y), ...t().howToRide },
      { art: (g, x, y, ms) => this.artCresting(g, x, y, ms), ...t().howToCrest },
      { art: (g, x, y) => this.artFoam(g, x, y), ...t().howToFoam },
      { art: (g, x, y, ms) => this.artTreasure(g, x, y, ms), ...t().howToTreasure },
      { art: (g, x, y, ms) => this.artHatchling(g, x, y, ms), ...t().howToHatchling },
      { art: (g, x, y) => this.artDolphin(g, x, y), ...t().howToDolphin },
      { art: (g, x, y, ms) => this.artJellyfish(g, x, y, ms), ...t().howToJelly },
    ];

    this.rows.forEach((row, i) => {
      const cy = ROW_TOP + i * ROW_H + ROW_H / 2;
      this.add
        .text(TEXT_X, cy - 20, row.title, {
          fontFamily: GOTHIC,
          fontSize: '17px',
          color: hex(PALETTE.ink),
        })
        .setOrigin(0, 0);
      const body = this.add
        .text(TEXT_X, cy + 4, '', {
          fontFamily: GOTHIC,
          fontSize: '14px',
          color: hex(PALETTE.inkDim),
          lineSpacing: 4,
        })
        .setOrigin(0, 0);
      this.wrap(body, row.body, VIEW_W - TEXT_X - 24);
    });

    // 色の意味は 2 つだけ。ここだけは覚えて帰ってもらう。
    //
    // **色の名前だけでは足りない。** 「ティール」「コーラル」は日本語では
    // 馴染みがなく、普通の呼び方（緑・オレンジ）に直しても、
    // 画面のどの色を指しているかは名前からは分からない。
    // **実際の色をその場に置いて、名前と並べる。**
    this.colorSwatchY = ROW_TOP + this.rows.length * ROW_H + 10;
    [t().howToColorGood, t().howToColorBad].forEach((line, i) => {
      this.add
        .text(SWATCH_X + 20, this.colorSwatchY + i * 24, line, {
          fontFamily: GOTHIC,
          fontSize: '14px',
          color: hex(PALETTE.ink),
        })
        .setOrigin(0, 0.5);
    });

    const back = this.add
      .text(VIEW_W / 2, VIEW_H - 76, t().back, {
        fontFamily: MONO,
        fontSize: '19px',
        color: hex(PALETTE.deep),
        backgroundColor: hex(PALETTE.swell),
        padding: { x: 30, y: 13 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    back.on('pointerdown', () => this.leave());
    this.input.keyboard?.on('keydown-ESC', () => this.leave());
  }

  private leave(): void {
    const { scene, ...rest } = this.back;
    this.scene.start(scene, rest);
  }

  update(): void {
    const g = this.g;
    g.clear();
    this.rows.forEach((row, i) => {
      const cy = ROW_TOP + i * ROW_H + ROW_H / 2;
      row.art(g, ART_X + ART_W / 2, cy, this.time.now);
    });

    // 色見本。名前の左に、本物と同じ色を置く。
    [PALETTE.swell, PALETTE.hazard].forEach((color, i) => {
      g.fillStyle(color, 1);
      g.fillCircle(SWATCH_X, this.colorSwatchY + i * 24, 7);
    });
  }

  /**
   * 幅に合わせて折り返す。
   *
   * **日本語は空白で折り返せない。** Phaser の `wordWrap` は空白でしか切らないので、
   * 日本語を渡すと 1 行のまま画面の外へ出ていく（実際にそうなった）。
   * 文字を 1 つずつ足して**実測**し、あふれる直前で改行する。
   * 行頭に来てはいけない字（句読点や閉じ括弧）は前の行に残す。
   */
  private wrap(text: Phaser.GameObjects.Text, raw: string, maxWidth: number): void {
    const lines: string[] = [];
    let line = '';
    for (const ch of raw) {
      text.setText(line + ch);
      if (line !== '' && text.width > maxWidth && !NO_LINE_START.has(ch)) {
        // **英語は語の途中で切らない。** 同じ処理で両方の言語を通すので、
        // 直前の空白まで戻れるなら戻る。日本語には空白が無いので字で切れる。
        const cut = line.lastIndexOf(' ');
        if (cut > 0 && !CJK.test(line.slice(cut))) {
          lines.push(line.slice(0, cut));
          line = line.slice(cut + 1) + ch;
        } else {
          lines.push(line);
          line = ch;
        }
      } else {
        line += ch;
      }
    }
    if (line !== '') lines.push(line);
    text.setText(lines.join('\n'));
  }

  // ── 絵 ────────────────────────────────────
  //
  // どれも本編（GameScene）と同じ描き方を、この幅に縮めたもの。

  /** 乗れる波。ティールの帯と峰。 */
  private artRideable(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
    g.fillStyle(PALETTE.swell, 0.16);
    g.fillRect(cx - ART_W / 2, cy - 22, ART_W, 26);
    g.lineStyle(3, PALETTE.swell, 0.9);
    this.wave(g, cx, cy + 4, 3);
  }

  /** 崩れる直前。峰にコーラルの線が重なって点滅する。 */
  private artCresting(
    g: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    timeMs: number,
  ): void {
    g.fillStyle(PALETTE.swell, 0.24);
    g.fillRect(cx - ART_W / 2, cy - 22, ART_W, 26);
    g.lineStyle(3, PALETTE.swell, 0.9);
    this.wave(g, cx, cy + 4, 3);
    g.lineStyle(2, PALETTE.hazard, 0.5 + Math.sin(timeMs / 140) * 0.25);
    this.wave(g, cx, cy - 1, 3);
  }

  /** 崩れの先頭。白泡の帯と、コーラルで縁取った危ない縁。 */
  private artFoam(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
    const left = cx - ART_W / 2;
    g.fillStyle(PALETTE.foam, 0.16);
    g.fillRect(left, cy - 2, ART_W, 24);
    g.fillStyle(PALETTE.foam, 0.55);
    g.fillRect(left, cy - 10, ART_W, 12);
    g.fillStyle(PALETTE.hazard, 0.3);
    g.fillRect(left, cy - 10, ART_W, 12);
    g.lineStyle(2.5, PALETTE.hazard, 0.9);
    this.wave(g, cx, cy - 10, 2.5);
  }

  private artTreasure(
    g: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    timeMs: number,
  ): void {
    const pulse = 1 + Math.sin(timeMs / 380) * 0.12;
    // 小さいものから大きいものへ。点の高さが違うことを大きさで見せる。
    const tiers = [
      { dx: -30, r: 5, color: PALETTE.foam },
      { dx: 0, r: 8, color: PALETTE.sand },
      { dx: 32, r: 11, color: PALETTE.hint },
    ];
    for (const tier of tiers) {
      g.fillStyle(tier.color, 0.22);
      g.fillCircle(cx + tier.dx, cy, tier.r * 2.1 * pulse);
      g.fillStyle(tier.color, 1);
      g.fillCircle(cx + tier.dx, cy, tier.r);
    }
  }

  private artHatchling(
    g: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    timeMs: number,
  ): void {
    // 本編より少し大きく描く。海の中と違って、ここは見比べる場所なので。
    drawTurtle(g, cx, cy, {
      strokePhase: timeMs / 190,
      rotation: 0,
      submerged: 0,
      alpha: 1,
      scale: 0.6,
    });
    g.lineStyle(1.5, PALETTE.sand, 0.4 + Math.sin(timeMs / 300) * 0.2);
    g.strokeCircle(cx, cy, 24);
  }

  private artDolphin(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
    g.fillStyle(PALETTE.swell, 0.25);
    g.fillCircle(cx, cy, 20);
    g.fillStyle(PALETTE.swell, 1);
    g.fillEllipse(cx, cy, 15, 30);
    g.fillTriangle(cx, cy + 14, cx - 9, cy + 24, cx + 9, cy + 24);
    g.fillStyle(PALETTE.foam, 0.85);
    g.fillEllipse(cx, cy - 9, 7, 9);
  }

  private artJellyfish(
    g: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    timeMs: number,
  ): void {
    const pulse = 0.8 + Math.sin(timeMs / 320) * 0.2;
    g.fillStyle(PALETTE.hazard, 0.32);
    g.fillCircle(cx, cy, 13 * pulse);
    g.fillStyle(PALETTE.hazard, 0.9);
    g.fillEllipse(cx, cy - 2, 15 * pulse, 12 * pulse);
    g.lineStyle(1.6, PALETTE.hazard, 0.75);
    for (let i = 0; i < 4; i++) {
      const tx = cx - 6 + i * 4;
      g.beginPath();
      g.moveTo(tx, cy + 4);
      for (let k = 1; k <= 3; k++) {
        g.lineTo(tx + Math.sin(timeMs / 260 + i + k) * 3, cy + 4 + k * 5);
      }
      g.strokePath();
    }
  }

  /** 波の線。本編と同じうねりで引く。 */
  private wave(g: Phaser.GameObjects.Graphics, cx: number, y: number, amp: number): void {
    const left = cx - ART_W / 2;
    g.beginPath();
    for (let x = 0; x <= ART_W; x += 6) {
      const py = y + Math.sin(x / 14 + this.time.now / 620) * amp;
      if (x === 0) g.moveTo(left + x, py);
      else g.lineTo(left + x, py);
    }
    g.strokePath();
  }
}
